import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Phone, Mail, MessageCircle, MapPin } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dogName: '',
    dogWeight: '',
    heatDate: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  const formDataToSend = new FormData();
  formDataToSend.append("access_key", "d79f68c8-8b07-435b-b7b2-6fea0dfede01");
  formDataToSend.append("name", formData.name);
  formDataToSend.append("email", formData.email);
  formDataToSend.append("phone", formData.phone);
  formDataToSend.append("dogName", formData.dogName);
  formDataToSend.append("dogWeight", formData.dogWeight);
  formDataToSend.append("heatDate", formData.heatDate);
  formDataToSend.append("message", formData.message);

  try {
    const response = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      body: formDataToSend
    });

    const data = await response.json();

    if (data.success) {
      toast.success("Message envoyé !", {
        description: "Nous vous contacterons très prochainement."
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        dogName: '',
        dogWeight: '',
        heatDate: '',
        message: ''
      });
    } else {
      toast.error("Erreur serveur", { description: "Merci de réessayer plus tard." });
    }
  } catch (error) {
    toast.error("Erreur réseau", { description: "Impossible de contacter le serveur." });
  }

  setIsSubmitting(false);
};

  return (
    <div className="min-h-screen bg-black pt-20">
      <Toaster richColors />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-black to-black/95">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            <span className="text-[#cbb26a]">Contactez</span>-nous
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Une question ? Besoin d'informations ? N'hésitez pas à nous contacter
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">
                Nos <span className="text-[#cbb26a]">coordonnées</span>
              </h2>
              
              <div className="space-y-6 mb-8">
                <Card className="bg-white/5 border-[#cbb26a]/30 hover:bg-white/10 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#cbb26a]/20 rounded-full flex items-center justify-center">
                        <Phone className="text-[#cbb26a]" size={24} />
                      </div>
                      <div>
                        <div className="text-white/70 text-sm mb-1">Téléphone</div>
                        <a href={`tel:${contactInfo.phone}`} className="text-white text-lg font-semibold hover:text-[#cbb26a] transition-colors duration-300">
                          {contactInfo.phone}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-[#cbb26a]/30 hover:bg-white/10 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#cbb26a]/20 rounded-full flex items-center justify-center">
                        <MessageCircle className="text-[#cbb26a]" size={24} />
                      </div>
                      <div>
                        <div className="text-white/70 text-sm mb-1">WhatsApp</div>
                        <a href={`https://wa.me/33${contactInfo.whatsapp.substring(1)}`} className="text-white text-lg font-semibold hover:text-[#cbb26a] transition-colors duration-300">
                          {contactInfo.whatsapp}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-[#cbb26a]/30 hover:bg-white/10 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#cbb26a]/20 rounded-full flex items-center justify-center">
                        <Mail className="text-[#cbb26a]" size={24} />
                      </div>
                      <div>
                        <div className="text-white/70 text-sm mb-1">Email</div>
                        <a href={`mailto:${contactInfo.email}`} className="text-white text-lg font-semibold hover:text-[#cbb26a] transition-colors duration-300">
                          {contactInfo.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/5 border-[#cbb26a]/30 hover:bg-white/10 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#cbb26a]/20 rounded-full flex items-center justify-center">
                        <MapPin className="text-[#cbb26a]" size={24} />
                      </div>
                      <div>
                        <div className="text-white/70 text-sm mb-1">Localisation</div>
                        <div className="text-white text-lg font-semibold">
                          {contactInfo.address}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-[#cbb26a]/10 border-[#cbb26a]/30">
                <CardContent className="p-6">
                  <h3 className="text-[#cbb26a] font-semibold mb-3">Horaires</h3>
                  <p className="text-white/80 text-sm">
                    Nous sommes disponibles 7j/7 pour répondre à vos questions. 
                    Les saillies se font sur rendez-vous uniquement.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">
                Formulaire de <span className="text-[#cbb26a]">réservation</span>
              </h2>
              
              <Card className="bg-white/5 border-[#cbb26a]/30">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-white mb-2 block">Votre nom *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-white/10 border-[#cbb26a]/30 text-white focus:border-[#cbb26a]"
                        placeholder="Votre nom complet"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-white mb-2 block">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="bg-white/10 border-[#cbb26a]/30 text-white focus:border-[#cbb26a]"
                          placeholder="votre@email.com"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-white mb-2 block">Téléphone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="bg-white/10 border-[#cbb26a]/30 text-white focus:border-[#cbb26a]"
                          placeholder="06XXXXXXXX"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="dogName" className="text-white mb-2 block">Nom de votre femelle</Label>
                        <Input
                          id="dogName"
                          name="dogName"
                          value={formData.dogName}
                          onChange={handleChange}
                          className="bg-white/10 border-[#cbb26a]/30 text-white focus:border-[#cbb26a]"
                          placeholder="Nom de la chienne"
                        />
                      </div>
                      <div>
                        <Label htmlFor="dogWeight" className="text-white mb-2 block">Poids (kg)</Label>
                        <Input
                          id="dogWeight"
                          name="dogWeight"
                          type="number"
                          value={formData.dogWeight}
                          onChange={handleChange}
                          className="bg-white/10 border-[#cbb26a]/30 text-white focus:border-[#cbb26a]"
                          placeholder="Poids en kg"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="heatDate" className="text-white mb-2 block">Date des chaleurs (estimée)</Label>
                      <Input
                        id="heatDate"
                        name="heatDate"
                        type="date"
                        value={formData.heatDate}
                        onChange={handleChange}
                        className="bg-white/10 border-[#cbb26a]/30 text-white focus:border-[#cbb26a]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-white mb-2 block">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="bg-white/10 border-[#cbb26a]/30 text-white focus:border-[#cbb26a] resize-none"
                        placeholder="Parlez-nous de votre projet, posez vos questions..."
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-[#cbb26a] text-black hover:bg-[#d4c485] transition-all duration-300"
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;